import React, {useEffect} from 'react'
import {Helmet} from 'react-helmet-async'
import {Link} from 'react-router-dom'
import {CmdkActionGetter} from '../../cmdk/types'
import {useSetCrumbs} from '../../components/crumbtrail'
import {DocsLinkIcon} from '../../components/docs-link-icon'
import {BetaFlagMarker, PreviewFlagMarker} from '../../components/feature-flags'
import {
  PageHeader,
  PageHeaderContent,
  PageTitle
} from '../../components/page-styles'
import {Button} from '../../components/library/buttons/button'
import {IconName} from '../../components/library/icon/icon'
import styled from 'styled-components'
import {EnvironmentNode} from '../../environment'
import {Flags, useFlags} from '../../hooks/use-flags'
import {useAnalytics} from '../../lib/analytics/analytics'

const ToolCategories = styled.div`
  padding: 20px 15px;
`

const CategoryDescription = styled.div`
  display: flex;
  flex-direction: row;
`

const CategoryLeftSection = styled.div`
  padding-top: 25px;
  /* must leave enough space for longest title */
  width: 150px;
  margin-right: 30px;
`

const ToolCard = styled.div`
  border-radius: ${p => p.theme.borderRadius};
  background: white;
  border: 1px solid ${p => p.theme.grey10};
  padding: 20px 30px 20px 20px;
  width: 343px;
`

const CategoryTools = styled.div`
  display: flex;
  flex-flow: row wrap;
  flex: 1;
  align-items: stretch;
  justify-content: flex-start;
  gap: 20px;
`

const ToolCategory = styled.div`
  background: ${p => p.theme.grey05};

  display: flex;
  flex-direction: row;
  align-items: stretch;

  margin-bottom: 20px;
  padding: 20px 15px;
  /* stop each row from sqashing description */
  min-width: 583px;

  border-radius: ${p => p.theme.borderRadius};
`

const CategoryTitle = styled.div`
  color: ${p => p.theme.grey100};
  font-size: ${p => p.theme.fontSize};
  line-height: ${p => p.theme.lineHeight};
  font-weight: bold;
`
const CardTitle = styled.div`
  display: flex;
  justify-content: space-between;
`

const CardDescription = styled.div`
  color: ${p => p.theme.grey100};
  font-size: ${p => p.theme.fontSizeS};
  margin-top: 10px;
  margin-left: 10px;
`

type CategoryTool = {
  title: string
  description: string
  link: string
  docLink?: string
  preview?: boolean
  beta?: boolean
}
type Category = {
  icon: IconName
  title: string
  elements: CategoryTool[]
}

export const Tools = ({environment}: {environment: EnvironmentNode}) => {
  const analytics = useAnalytics()

  useEffect(() => {
    analytics.track({
      event: 'Viewed tools home',
      properties: {
        category: 'tools',
        org_id: environment.organisationId,
        environment_id: environment.id
      }
    })
  }, [analytics, environment])

  const flags = useFlags(environment.organisationId)
  useSetCrumbs(['Tools'])

  const toolCategories: JSX.Element[] = []
  for (const category of categories) {
    const tools = category.elements.filter(flagFilter(flags)).map(tool => (
      <ToolCard key={tool.title}>
        <CardTitle>
          <Link to={tool.link}>
            <Button appearance="transparent">{tool.title}</Button>
            {tool?.preview ? <PreviewFlagMarker /> : null}
            {tool?.beta ? <BetaFlagMarker /> : null}
          </Link>
          {tool.docLink && (
            <DocsLinkIcon
              to={tool.docLink}
              iconHeight="25px"
              iconWidth="25px"
            />
          )}
        </CardTitle>
        <CardDescription>{tool.description}</CardDescription>
      </ToolCard>
    ))

    if (tools.length) {
      toolCategories.push(
        <ToolCategory key={`category-${category.title}`}>
          <CategoryLeftSection>
            <CategoryDescription>
              {/* <IconWrapper>
                <Icon name={category.icon} />
              </IconWrapper> */}
              <CategoryTitle>{category.title}</CategoryTitle>
            </CategoryDescription>
          </CategoryLeftSection>
          <CategoryTools>{tools}</CategoryTools>
        </ToolCategory>
      )
    }
  }

  return (
    <>
      <Helmet>
        <title>Tools</title>
      </Helmet>
      <PageHeader>
        <PageHeaderContent>
          <PageTitle>Tools</PageTitle>
        </PageHeaderContent>
      </PageHeader>
      <ToolCategories>{toolCategories}</ToolCategories>
    </>
  )
}

const flagFilter = (flags?: Flags) => (element: CategoryTool) =>
  !!flags && (!element.preview || flags.featurePreview)

export const getActionsForToolbar: CmdkActionGetter = flags =>
  categories.flatMap(category =>
    category.elements.filter(flagFilter(flags)).map(element => ({
      name: element.title,
      to: element.link.replace('../', ''),
      parent: 'tools'
    }))
  )

const categories: Category[] = [
  {
    icon: 'settings_nav',
    title: 'Configuration',
    elements: [
      {
        title: 'SDK request debugger',
        description:
          'View recent client API requests, to help with debugging your SDK integration.',
        link: '../../activity-log'
      },
      {
        title: 'Analytics debugger',
        description:
          'View and download the last 1,000 analytics events received.',
        link: '../analytics',
        docLink:
          'https://documentation.atomic.io/workbench-guide/tools#analytics-debugger'
      }
    ]
  },
  {
    icon: 'configure',
    title: 'Testing',
    elements: [
      {
        title: 'Test Action Flow sender',
        description: 'Batch and send test Action Flows.',
        link: '../testing-tools/batch-send',
        docLink:
          'https://documentation.atomic.io/tutorials/sending-cards#test-cards'
      },
      {
        title: 'Validate auth token',
        description: 'Validate an auth token against an API key.',
        link: '../testing-tools/validate-auth-token',
        docLink: 'https://documentation.atomic.io/sdks/auth-SDK'
      }
    ]
  },
  {
    icon: 'configure',
    title: 'Operations',
    elements: [
      {
        title: 'Analytics exporter',
        description: 'Batched analytics files available to download.',
        link: '../analytics-exporter',
        docLink:
          'https://documentation.atomic.io/api/analytics#workbench-download'
      },
      {
        title: 'Customer data importer',
        description:
          'Add or update user profile records by uploading a CSV file. See import history.',
        link: '../customer-csv-importer',
        // TBA
        docLink: 'https://documentation.atomic.io/workbench-guide/customers'
      },
      {
        title: 'Cancel cards',
        description: 'Find and cancel active card instances in bulk.',
        link: '../cancel-cards',
        docLink: 'https://documentation.atomic.io/advanced/cancel-cards'
      },
      {
        title: 'Action Flow bulk send',
        description: 'Upload a CSV file to send Action Flows in bulk.',
        link: '../af-bulk-send',
        docLink:
          'https://documentation.atomic.io/workbench/tools#action-flow-bulk-send'
      }
    ]
  },
  {
    icon: 'configure',
    title: 'Security',
    elements: [
      {
        beta: true,
        title: 'Audit log',
        description: 'Log of user actions within your organisation.',
        link: '../../audit-log'
      }
    ]
  }
]
